'use client';
import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  Target,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  Cpu,
  BookOpen,
  BarChart2,
  Clock,
} from 'lucide-react';

type TradeType = 'Intraday' | 'Swing' | 'Long-term';
type SetupType = 'Breakout' | 'Reversal' | 'Momentum';
type RiskLevel = 'Low' | 'Medium' | 'High';
type TrendDir = 'up' | 'down';

interface Trade {
  id: string;
  symbol: string;
  name: string;
  market: string;
  exchange: string;
  cmp: number;
  entry: string;
  stopLoss: number;
  target1: number;
  target2: number;
  rr: string;
  type: TradeType;
  setup: SetupType;
  risk: RiskLevel;
  confidence: number;
  sector: string;
  indicators: string[];
  aiAnalysis: string;
  beginnerNote: string;
  trend: TrendDir;
  volume: string;
  lastUpdated: string;
}

interface TradeCardProps {
  trade: Trade;
  beginnerMode: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}

const typeClass: Record<TradeType, string> = {
  Intraday: 'badge-intraday',
  Swing: 'badge-swing',
  'Long-term': 'badge-longterm',
};

const setupClass: Record<SetupType, string> = {
  Breakout: 'badge-breakout',
  Reversal: 'badge-reversal',
  Momentum: 'badge-momentum',
};

const riskConfig: Record<RiskLevel, { color: string; bg: string }> = {
  Low: { color: 'var(--positive)', bg: 'rgba(0,212,170,0.1)' },
  Medium: { color: 'var(--warning)', bg: 'rgba(246,173,85,0.1)' },
  High: { color: 'var(--negative)', bg: 'rgba(245,101,101,0.1)' },
};

function ConfidenceRing({ confidence }: { confidence: number }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (confidence / 100) * circumference;
  const color = confidence >= 80 ? 'var(--primary)' : confidence >= 65 ? 'var(--warning)' : 'var(--negative)';

  return (
    <div className="relative w-14 h-14 flex items-center justify-center flex-shrink-0">
      <svg width="56" height="56" className="confidence-ring" aria-hidden="true">
        <circle cx="28" cy="28" r={radius} fill="none" stroke="var(--border)" strokeWidth="3" />
        <circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs font-mono-nums font-bold" style={{ color, lineHeight: 1 }}>
          {confidence}%
        </span>
        <span className="text-xs" style={{ color: 'var(--muted-foreground)', fontSize: '8px' }}>
          AI
        </span>
      </div>
    </div>
  );
}

export default function TradeCard({ trade, beginnerMode, expanded, onToggleExpand }: TradeCardProps) {
  const rc = riskConfig[trade.risk];

  return (
    <div
      className="rounded-xl flex flex-col transition-all duration-200 hover:shadow-card-hover"
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
      }}
    >
      {/* Header */}
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-base font-bold font-mono-nums">{trade.symbol}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeClass[trade.type]}`}
            >
              {trade.type}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${setupClass[trade.setup]}`}
            >
              {trade.setup}
            </span>
          </div>
          <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
            {trade.name} · {trade.exchange}
          </p>
        </div>
        <ConfidenceRing confidence={trade.confidence} />
      </div>

      {/* CMP + Change */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
      >
        <div>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.04em' }}>
            CMP
          </p>
          <p className="text-xl font-mono-nums font-bold">
            {typeof trade.cmp === 'number' && trade.cmp < 200
              ? trade.cmp.toFixed(4)
              : trade.cmp.toLocaleString('en-IN')}
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 justify-end">
            {trade.trend === 'up' ? (
              <TrendingUp size={14} className="text-positive" />
            ) : (
              <TrendingDown size={14} className="text-negative" />
            )}
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded font-mono-nums ${
                trade.trend === 'up' ? 'bg-positive-tint text-positive' : 'bg-negative-tint text-negative'
              }`}
            >
              Vol {trade.volume}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1 justify-end">
            <span className="text-xs px-1.5 py-0.5 rounded-full" style={rc}>
              {trade.risk} Risk
            </span>
          </div>
        </div>
      </div>

      {/* Trade Levels */}
      <div className="p-4 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs mb-1" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.04em' }}>
            Entry Zone
          </p>
          <p className="text-sm font-mono-nums font-semibold">{trade.entry}</p>
        </div>
        <div>
          <p className="text-xs mb-1 flex items-center gap-1" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.04em' }}>
            <ShieldAlert size={10} />
            Stop Loss
          </p>
          <p className="text-sm font-mono-nums font-semibold text-negative">
            {trade.stopLoss.toLocaleString('en-IN')}
          </p>
        </div>
        <div>
          <p className="text-xs mb-1 flex items-center gap-1" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.04em' }}>
            <Target size={10} />
            Target 1
          </p>
          <p className="text-sm font-mono-nums font-semibold text-positive">
            {trade.target1.toLocaleString('en-IN')}
          </p>
        </div>
        <div>
          <p className="text-xs mb-1 flex items-center gap-1" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.04em' }}>
            <Target size={10} />
            Target 2
          </p>
          <p className="text-sm font-mono-nums font-semibold text-positive">
            {trade.target2.toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* R:R + Indicators */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-2">
          <BarChart2 size={12} style={{ color: 'var(--muted-foreground)' }} />
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>R:R</span>
          <span className="text-sm font-mono-nums font-semibold text-positive">{trade.rr}</span>
        </div>
        <div className="flex gap-1 flex-wrap justify-end">
          {trade.indicators.slice(0, 2).map((ind) => (
            <span
              key={`ind-${trade.id}-${ind}`}
              className="text-xs px-1.5 py-0.5 rounded"
              style={{ background: 'var(--muted)', color: 'var(--muted-foreground)', fontSize: '10px' }}
            >
              {ind}
            </span>
          ))}
        </div>
      </div>

      {/* AI Analysis Expandable */}
      <div style={{ borderTop: '1px solid var(--border)' }}>
        <button
          onClick={onToggleExpand}
          className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-medium transition-colors duration-150 hover:bg-muted/30"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <div className="flex items-center gap-1.5">
            {beginnerMode ? <BookOpen size={12} /> : <Cpu size={12} />}
            <span>{beginnerMode ? 'Beginner Explanation' : 'AI Analysis'}</span>
          </div>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>

        {expanded && (
          <div
            className="px-4 pb-4 text-xs leading-relaxed animate-fade-in"
            style={{ color: 'var(--muted-foreground)' }}
          >
            {beginnerMode ? trade.beginnerNote : trade.aiAnalysis}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ borderTop: '1px solid var(--border)', background: 'var(--surface-3)', borderRadius: '0 0 0.75rem 0.75rem' }}
      >
        <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
          <Clock size={10} />
          <span>Updated {trade.lastUpdated}</span>
        </div>
        <span
          className="text-xs px-2 py-0.5 rounded"
          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
        >
          {trade.sector}
        </span>
      </div>
    </div>
  );
}